import { useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert } from '@ds/feedback';
import { SegmentedControl } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { CATEGORIAS_DA_API, ENDPOINTS_DA_API, curlDoEndpoint } from '@/data/apiDaClinica';
import type { CategoriaDaApi, EndpointDaApi } from '@/data/apiDaClinica';
import { dados } from '@/data/porta';
import { ChavesDeApi } from './ChavesDeApi';
import { WebhooksDaClinica } from './WebhooksDaClinica';

const CORES: Record<EndpointDaApi['metodo'], 'success' | 'info' | 'warning' | 'danger'> = {
    GET: 'info',
    POST: 'success',
    PATCH: 'warning',
    DELETE: 'danger',
};

function base(): string {
    return import.meta.env.VITE_SUPABASE_URL ?? 'https://api.formulamed.com.br';
}

function chaveAnon(): string {
    const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
    return chave ? `${String(chave).slice(0, 24)}…` : 'SUA_CHAVE_PUBLICA';
}

function Bloco({ children }: { children: string }) {
    return (
        <pre
            style={{
                margin: 0, padding: 12, overflowX: 'auto',
                background: 'var(--surface-secondary)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', fontSize: 12, lineHeight: '18px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
        >
            {children}
        </pre>
    );
}

/**
 * A coleção de endpoints da clínica.
 *
 * Existe porque o endpoint já existia e o endereço dele não estava escrito em
 * lugar nenhum. É catálogo curado, não o OpenAPI cru: o PostgREST publica 82
 * caminhos — toda tabela e todo filtro —, e entregar aquilo seria entregar o
 * esquema do banco no lugar de uma API.
 *
 * O que impede a página de envelhecer é o botão de conferir: ele lê o que o
 * servidor publica e marca o que sumiu. Documentação que mente é pior que
 * documentação que falta.
 */
export function ApiDaClinica() {
    const aviso = useAviso();
    const [categoria, setCategoria] = useState<CategoriaDaApi>('autenticacao');
    const [ausentes, setAusentes] = useState<string[] | null>(null);
    const [conferindo, setConferindo] = useState(false);

    const conferir = async () => {
        setConferindo(true);
        try {
            const caminhos = await dados().caminhosDaApi();
            if (caminhos.length === 0) {
                aviso({
                    title: 'Não há servidor para conferir',
                    description: 'Esta instalação está no modo de demonstração.',
                    tone: 'info',
                });
                setAusentes(null);
                return;
            }
            /* O OpenAPI do PostgREST publica só o que é dele: o que vive no
               GoTrue e na borda não aparece lá, e cobrá-lo daria um alarme
               falso a cada conferência. */
            const doRest = ENDPOINTS_DA_API.filter((e) => e.servico === 'rest');
            /* Sem repetir: dois endpoints do mesmo caminho (listar e criar em
               `/contatos`) acusariam o mesmo problema duas vezes. */
            const faltando = [...new Set(
                doRest.filter((e) => !caminhos.includes(e.caminhoCru)).map((e) => e.caminhoCru),
            )];
            setAusentes(faltando);
            aviso({
                title: faltando.length ? `${faltando.length} caminho(s) não existem mais` : 'Catálogo confere com o servidor',
                description: faltando.length ? faltando.join(', ') : `${doRest.length} caminhos verificados`,
                tone: faltando.length ? 'danger' : 'success',
            });
        } catch (e) {
            aviso({ title: 'Não foi possível conferir', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setConferindo(false);
        }
    };

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: 'Copiado' });
        } catch {
            /* Sem permissão de área de transferência, o texto continua à vista
               para copiar à mão — e dizer que copiou seria mentira. */
            aviso({ title: 'Não foi possível copiar', description: 'Selecione o texto e copie à mão.', tone: 'warning' });
        }
    };

    const daCategoria = ENDPOINTS_DA_API.filter((e) => e.categoria === categoria);
    const descricao = CATEGORIAS_DA_API.find((c) => c.id === categoria);

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="API e integrações"
                description="Os endereços para ligar a Fórmula MED ao seu outro sistema, com exemplo pronto."
                actions={(
                    <Button variant="secondary" onClick={conferir} disabled={conferindo} iconLeft={<Icon name="shield-check" size={15} />}>
                        {conferindo ? 'Conferindo…' : 'Conferir contra o servidor'}
                    </Button>
                )}
            >
                <SegmentedControl
                    ariaLabel="Categorias da API"
                    value={categoria}
                    onChange={(v) => setCategoria(v as CategoriaDaApi)}
                    options={CATEGORIAS_DA_API.map((c) => ({ value: c.id, label: c.rotulo }))}
                />
            </PageHeader>

            <Card title="Antes de começar" padding={18}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, lineHeight: '19px' }}>
                    <p style={{ margin: 0 }}>
                        Base desta instalação: <code>{base()}</code>
                    </p>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                        Toda chamada leva dois cabeçalhos: <code>apikey</code>, com a chave pública, e{' '}
                        <code>Authorization: Bearer</code>, com o token de quem entrou. A chave pública sozinha
                        não abre nada — quem decide o que se vê é o token, e o que ele vê é o que aquela pessoa
                        veria na tela.
                    </p>
                    <Alert tone="neutral" dismissible={false} title="Conta de pessoa ou chave de máquina">
                        Os endereços abaixo usam a conta de uma pessoa, com as permissões dela, e o token vale uma
                        hora (renove pelo <code>refresh_token</code>). Para um sistema que só precisa LER o catálogo, as
                        unidades e os horários ocupados, use uma chave de API — é estreita de propósito, e a
                        proprietária a gera e revoga logo aqui.
                    </Alert>
                </div>
            </Card>

            <ChavesDeApi />

            <WebhooksDaClinica />

            {ausentes && ausentes.length > 0 && (
                <Alert tone="danger" dismissible={false} title="Estes caminhos não existem mais no servidor">
                    {ausentes.join(', ')}
                </Alert>
            )}

            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{descricao?.descricao}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {daCategoria.map((e) => (
                    <Card key={e.id} padding={18}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <Badge tone={CORES[e.metodo]}>{e.metodo}</Badge>
                                <strong style={{ fontSize: 14, fontWeight: 600 }}>{e.titulo}</strong>
                                <code style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{e.caminho}</code>
                                <span style={{ marginLeft: 'auto' }}>
                                    <Button size="sm" variant="ghost" onClick={() => copiar(curlDoEndpoint(e, base(), chaveAnon()))}>
                                        Copiar curl
                                    </Button>
                                </span>
                            </div>

                            <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                                {e.descricao}
                            </p>

                            {e.corpo && (
                                <div>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                        Corpo
                                    </span>
                                    <Bloco>{JSON.stringify(e.corpo, null, 2)}</Bloco>
                                </div>
                            )}

                            {e.resposta !== undefined && (
                                <div>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                        Resposta
                                    </span>
                                    <Bloco>{JSON.stringify(e.resposta, null, 2)}</Bloco>
                                </div>
                            )}

                            {e.atencao && (
                                <Alert tone="warning" dismissible={false}>{e.atencao}</Alert>
                            )}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
